import {
    Box,
    Breadcrumbs,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import AddIcon from '@mui/icons-material/Add';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { KitType } from '../actions/getKitTypes';
import { sendAdminRequest, sendRequest } from '../api/websocketApi';
import UpdateLabProcessStatusForm from '../components/admin/UpdateLabStatusForm';
import Button from '../components/Button';
import CustomTooltip from '../components/CustomTooltip';
import FileCard from '../components/FileCard';
import KitProgress from '../components/KitProgress';
import Link from '../components/Link';
import NewKitForm from '../components/NewKitForm';
import RegisterKitForm from '../components/RegisterKitForm';
import StatusPill from '../components/StatusPill';
import TestName from '../components/TestName';
import UpdateTRFDialog from '../components/UpdateTRFDialog';
import UploadTestFilesDialog from '../components/UploadTestFilesDialog';
import ViewTRFDialog from '../components/ViewTRFDialog';
import useKits, { Kit, LabProcessStatus } from '../hooks/useKits';
import useStore from '../store/store';

export interface KitFilesObject {
    KitId: number;
    Reports: File[];
    RawDataFiles: File[];
}

export interface File {
    FilePath: string;
    FileName: string;
    ReleaseDate: Date;
}

export const getKitDisplayName = (
    kit: Kit | undefined,
    kitTypes: KitType[]
): string => {
    if (!kit) return '';
    return (
        kit.Nickname ??
        kitTypes.find((kitType) => kitType.KitTypeId === kit.KitTypeId)?.Type ??
        'Unknown'
    );
};

export const getKitById = (kits: Kit[], kitId: number): Kit | undefined => {
    return kits.find((kit) => kit.KitId === kitId);
};

const MemberPage = () => {
    const location = useLocation();
    const isAdmin = location.state?.isAdmin ?? false;
    const member = location.state?.member;
    const [tabIndex, setTabIndex] = useState(0);
    const kitTypes = useStore((state) => state.kitTypes);
    const [kitFiles, setKitFiles] = useState<KitFilesObject[]>([]);
    const [newTestDialogOpen, setNewTestDialogOpen] = useState(false);
    const [testAdded, setTestAdded] = useState(false);
    const [registerKitDialogOpen, setRegisterKitDialogOpen] = useState(false);
    const [updateLabStatusDialogOpen, setUpdateLabStatusDialogOpen] =
        useState(false);
    const [kit, setKit] = useState<Kit | undefined>(undefined);
    const [kitRegistered, setKitRegistered] = useState(false);
    const classes = useStyles({ url: process.env.PUBLIC_URL || '' });
    const [uploadTestFilesDialogOpen, setUploadTestFilesDialogOpen] =
        useState(false);
    const [testFileUploaded, setTestFileUploaded] = useState(false);
    const [viewTRFDialogOpen, setViewTRFDialogOpen] = useState(false);
    const [isUpdateTRFDialogOpen, setIsUpdateTRFDialogOpen] = useState(false);

    const [kitProgressOpen, setKitProgressOpen] = useState(false);
    const navigate = useNavigate();
    const kits = useKits({
        memberId: member?.MemberId ?? 0,
        reloadTrigger: testAdded || kitRegistered,
        isAdmin: isAdmin,
    });

    const handleNewTestClick = () => setNewTestDialogOpen(true);
    const handleNewTestDialogClose = () => setNewTestDialogOpen(false);
    const handleTestAdded = () => setTestAdded(true);

    const handleRegisterKitClick = (kit: Kit) => {
        setKit(kit);
        setRegisterKitDialogOpen(true);
    };

    const handleRegisterKitDialogClose = () => {
        setRegisterKitDialogOpen(false);
        setKit(undefined);
    };

    const handleUpdateLabStatusClick = (kit: Kit) => {
        if (!isAdmin) return;
        setKit(kit);
        setUpdateLabStatusDialogOpen(true);
    };

    const handleUpdateLabStatusDialogClose = () => {
        setUpdateLabStatusDialogOpen(false);
        setKit(undefined);
    };

    const handleKitRegistered = () => {
        setKitRegistered(true);
    };

    const handleUploadFilesClick = (kit: Kit) => {
        setKit(kit);
        setUploadTestFilesDialogOpen(true);
    };

    const handleUploadTestFilesDialogClose = () => {
        setUploadTestFilesDialogOpen(false);
        setKit(undefined);
    };

    const handleTestFilesUploaded = () => {
        setTestFileUploaded(true);
    };

    useEffect(() => {
        setTestAdded(false);
        setKitRegistered(false);
        setTestFileUploaded(false);
    }, [kits, testAdded, kitRegistered, testFileUploaded, member?.MemberId]);

    useEffect(() => {
        interface APIResponse {
            success: boolean;
            errCode?: 'invalid_token' | 'auth_failed' | 'access_denied';
            errDetails?: string;
            reports: File[];
            rawDataFiles: File[];
        }

        if (!kits?.length) return;

        const newFiles: KitFilesObject[] = [];
        let pendingRequests = kits.length;

        const handleResponse = (kitId: number) => (response: APIResponse) => {
            if (response.success && response.reports && response.rawDataFiles) {
                newFiles.push({
                    KitId: kitId,
                    Reports: response.reports,
                    RawDataFiles: response.rawDataFiles,
                });
            }
            pendingRequests--;
            if (pendingRequests === 0) {
                setKitFiles(newFiles);
            }
        };

        for (const kit of kits) {
            const config = {
                endpoint: '/kit/get-files',
                body: { kitId: kit.KitId },
            };
            const callback = handleResponse(kit.KitId);

            if (isAdmin) {
                sendAdminRequest(config, callback);
            } else {
                sendRequest(config, callback);
            }
        }
    }, [kits, testFileUploaded, isAdmin]);

    const getAge = (birthDate: string | Date) => {
        const birth = new Date(birthDate);
        const today = new Date();
        const age = today.getFullYear() - birth.getFullYear();
        const isBirthdayPassed =
            today.getMonth() > birth.getMonth() ||
            (today.getMonth() === birth.getMonth() &&
                today.getDate() >= birth.getDate());
        return isBirthdayPassed ? age : age - 1;
    };

    const handleTabChange = (
        event: React.ChangeEvent<{}>,
        newValue: number
    ) => {
        setTabIndex(newValue);
    };

    interface TabPanelProps {
        children?: React.ReactNode;
        index: number;
        value: number;
    }

    function CustomTabPanel(props: TabPanelProps) {
        const { children, value, index, ...other } = props;

        return (
            <div
                role="tabpanel"
                hidden={value !== index}
                id={`simple-tabpanel-${index}`}
                aria-labelledby={`simple-tab-${index}`}
                className={classes.tabContent}
                {...other}
            >
                {value === index && <div>{children}</div>}
            </div>
        );
    }

    CustomTabPanel.propTypes = {
        children: PropTypes.node,
        index: PropTypes.number.isRequired,
        value: PropTypes.number.isRequired,
    };

    function a11yProps(index: number) {
        return {
            id: `simple-tab-${index}`,
            'aria-controls': `simple-tabpanel-${index}`,
        };
    }

    const breadcrumbs = member && (
        <Breadcrumbs aria-label="breadcrumb">
            <p
                color="inherit"
                onClick={() => navigate(-1)}
                style={{
                    textDecoration: 'underline',
                    cursor: 'pointer',
                }}
            >
                Members
            </p>
            <Typography color="textPrimary" style={{ fontWeight: 700 }}>
                {member.FirstName || 'N/A'} {member.LastName || 'N/A'}
            </Typography>
        </Breadcrumbs>
    );

    const showSampleId = (kit: Kit) => {
        if (kit.SampleId) {
            if (isAdmin && kit.TrfFilePath) {
                let tooltip = 'Verify TRF';
                if (kit.TrfFileStatus === 'valid') {
                    tooltip = 'Valid TRF';
                }
                if (kit.TrfFileStatus === 'invalid') {
                    tooltip = 'Invalid TRF';
                }
                return (
                    <CustomTooltip
                        title={tooltip}
                        variant="light"
                        placement="top"
                    >
                        <span>
                            <Link
                                onClick={() => {
                                    setKit(kit);
                                    setViewTRFDialogOpen(true);
                                }}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyItems: 'center',
                                    }}
                                >
                                    {kit.SampleId}
                                    {kit.TrfFileStatus === 'submitted' && (
                                        <NewReleasesIcon
                                            className={classes.newIcon}
                                        />
                                    )}
                                    {kit.TrfFileStatus === 'valid' && (
                                        <CheckCircleIcon
                                            className={classes.validIcon}
                                        />
                                    )}
                                    {kit.TrfFileStatus === 'invalid' && (
                                        <CancelIcon
                                            className={classes.invalidIcon}
                                        />
                                    )}
                                </div>
                            </Link>
                        </span>
                    </CustomTooltip>
                );
            } else {
                if (kit.TrfFileStatus === 'invalid') {
                    return (
                        <div
                            style={{
                                float: 'right',
                            }}
                        >
                            <Button
                                onClick={() => {
                                    setKit(kit);
                                    setIsUpdateTRFDialogOpen(true);
                                }}
                                icon={<AddIcon />}
                                size="small"
                            >
                                Reupload TRF
                            </Button>
                        </div>
                    );
                }
                return kit.SampleId;
            }
        } else {
            return (
                <div
                    style={{
                        float: 'right',
                    }}
                >
                    <Button
                        onClick={() => handleRegisterKitClick(kit)}
                        icon={<AddIcon />}
                        size="small"
                    >
                        Register Test
                    </Button>
                </div>
            );
        }
    };

    return (
        member && (
            <>
                <div className={classes.root}>
                    <div className={classes.top}>
                        <div className={classes.memberNav}>
                            <Breadcrumbs
                                separator={
                                    <NavigateNextIcon fontSize="small" />
                                }
                                aria-label="breadcrumb"
                                className={classes.breadcrumbs}
                            >
                                {breadcrumbs}
                            </Breadcrumbs>
                            <div
                                onClick={() => navigate(-1)}
                                className={classes.backButton}
                            >
                                Go back
                            </div>
                        </div>
                        <div className={classes.memberProfile}>
                            <div className={classes.memberProfileContainer}>
                                <div className={classes.profilePicture}>
                                    <img
                                        alt="Member"
                                        className={classes.face}
                                        src={
                                            process.env.PUBLIC_URL + '/face.svg'
                                        }
                                    />
                                </div>
                                <div className={classes.memberDetails}>
                                    <div className={classes.header}>
                                        <div className={classes.name}>
                                            {member.FirstName} {member.LastName}
                                        </div>
                                        <div>
                                            <span className={classes.label}>
                                                Patient ID:
                                            </span>{' '}
                                            <span className={classes.data}>
                                                {member.PatientId}
                                            </span>
                                        </div>
                                    </div>
                                    <div className={classes.patientInfo}>
                                        <div className={classes.patientInfoRow}>
                                            <span className={classes.label}>
                                                Age:
                                            </span>
                                            <span className={classes.data}>
                                                {getAge(member.BirthDate)}
                                            </span>
                                            <span className={classes.label}>
                                                Gender:
                                            </span>
                                            <span className={classes.data}>
                                                {member.Sex.charAt(
                                                    0
                                                ).toUpperCase() +
                                                    member.Sex.slice(1)}
                                            </span>
                                            <span className={classes.label}>
                                                Birth Date:
                                            </span>
                                            <span className={classes.data}>
                                                {new Date(
                                                    member.BirthDate
                                                ).toLocaleDateString()}
                                            </span>
                                        </div>
                                        <div className={classes.patientInfoRow}>
                                            <span className={classes.label}>
                                                Ethicity/Origin:
                                            </span>
                                            <span className={classes.data}>
                                                {member.Ethnicity || 'N/A'}
                                            </span>
                                            <span className={classes.label}>
                                                Referring physician:
                                            </span>
                                            <span className={classes.data}>
                                                {member.ReferringPhysician ??
                                                    'N/A'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className={classes.frame218}>
                                <Button
                                    icon={<AddIcon />}
                                    onClick={handleNewTestClick}
                                >
                                    Order Test
                                </Button>
                            </div>
                        </div>
                        <div className={classes.frame140}></div>
                    </div>

                    <Box
                        sx={{
                            width: '100%',
                            margin: '20px 0px 0px 20px',
                        }}
                    >
                        <Box
                            sx={{
                                borderBottom: 1,
                                borderColor: 'divider',
                            }}
                        >
                            <Tabs
                                value={tabIndex}
                                onChange={handleTabChange}
                                aria-label="files"
                                TabIndicatorProps={{
                                    style: { display: 'none' },
                                }}
                                textColor="primary"
                            >
                                <Tab
                                    className={`${classes.tab} ${tabIndex === 0 ? classes.activeTab : ''}`}
                                    label="Tests"
                                    {...a11yProps(0)}
                                />
                                <Tab
                                    className={`${classes.tab} ${tabIndex === 1 ? classes.activeTab : ''}`}
                                    label="Reports"
                                    {...a11yProps(1)}
                                />
                                <Tab
                                    className={`${classes.tab} ${tabIndex === 2 ? classes.activeTab : ''}`}
                                    label="Raw Data"
                                    {...a11yProps(2)}
                                />
                                <Tab
                                    className={`${classes.tab} ${tabIndex === 3 ? classes.activeTab : ''}`}
                                    label="Medical Records/Reports"
                                    {...a11yProps(3)}
                                />
                            </Tabs>
                        </Box>
                        <CustomTabPanel value={tabIndex} index={0}>
                            <TableContainer className={classes.tableContainer}>
                                <Table
                                    className={classes.table}
                                    aria-label="tests"
                                >
                                    <TableHead>
                                        <TableRow
                                            className={classes.tableHeadRow}
                                        >
                                            <TableCell
                                                className={
                                                    classes.tableCellHead
                                                }
                                            >
                                                Test name
                                            </TableCell>
                                            <TableCell
                                                align="center"
                                                className={
                                                    classes.tableCellHead
                                                }
                                            >
                                                Test ID
                                            </TableCell>
                                            <TableCell
                                                align="right"
                                                className={
                                                    classes.tableCellHead
                                                }
                                            >
                                                Sample ID
                                            </TableCell>
                                            <TableCell
                                                align="center"
                                                className={
                                                    classes.tableCellHead
                                                }
                                            >
                                                Status
                                            </TableCell>
                                            <TableCell
                                                align="center"
                                                className={
                                                    classes.tableCellHead
                                                }
                                            >
                                                Reports & Data
                                            </TableCell>
                                            <TableCell
                                                align="right"
                                                className={
                                                    classes.tableCellHead
                                                }
                                            >
                                                Last Update
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {Array.isArray(kits) &&
                                            kits.map((kit) => {
                                                return (
                                                    <TableRow key={kit.KitId}>
                                                        <TableCell
                                                            component="th"
                                                            scope="row"
                                                            className={
                                                                classes.tableCell
                                                            }
                                                        >
                                                            <TestName
                                                                status={
                                                                    kit.LabProcessStatus
                                                                }
                                                                onClick={() => {
                                                                    setKitProgressOpen(
                                                                        true
                                                                    );
                                                                    setKit(kit);
                                                                }}
                                                                variant="member"
                                                            >
                                                                <span>
                                                                    {(kit &&
                                                                        kit.Nickname) ||
                                                                        kitTypes.find(
                                                                            (
                                                                                kitType: KitType
                                                                            ) =>
                                                                                kitType.KitTypeId ===
                                                                                kit.KitTypeId
                                                                        )
                                                                            ?.Type ||
                                                                        'Unknown'}
                                                                </span>
                                                            </TestName>
                                                        </TableCell>
                                                        <TableCell
                                                            component="th"
                                                            scope="row"
                                                            className={
                                                                classes.tableCell
                                                            }
                                                            align="center"
                                                        >
                                                            {kitTypes.find(
                                                                (
                                                                    kitType: KitType
                                                                ) =>
                                                                    kitType.KitTypeId ===
                                                                    kit.KitTypeId
                                                            )
                                                                ?.KitTypeCatalogueId ||
                                                                'N/A'}
                                                        </TableCell>
                                                        <TableCell
                                                            align="right"
                                                            className={
                                                                classes.tableCell
                                                            }
                                                        >
                                                            {showSampleId(kit)}
                                                        </TableCell>
                                                        <TableCell
                                                            align="center"
                                                            className={
                                                                classes.tableCell
                                                            }
                                                        >
                                                            <StatusPill
                                                                status={
                                                                    kit.LabProcessStatus
                                                                }
                                                                isAdmin={
                                                                    isAdmin
                                                                }
                                                                onClick={() =>
                                                                    handleUpdateLabStatusClick(
                                                                        kit
                                                                    )
                                                                }
                                                            ></StatusPill>
                                                        </TableCell>
                                                        <TableCell
                                                            align="center"
                                                            className={
                                                                classes.tableCell
                                                            }
                                                        >
                                                            {kit.LabProcessStatus ===
                                                                LabProcessStatus.Released &&
                                                                !isAdmin && (
                                                                    <Link
                                                                        onClick={() =>
                                                                            setTabIndex(
                                                                                1
                                                                            )
                                                                        }
                                                                    >
                                                                        download
                                                                    </Link>
                                                                )}
                                                            {isAdmin && (
                                                                <Button
                                                                    onClick={() =>
                                                                        handleUploadFilesClick(
                                                                            kit
                                                                        )
                                                                    }
                                                                    disabled={
                                                                        !kit.SampleId
                                                                    }
                                                                    size="small"
                                                                >
                                                                    Upload
                                                                </Button>
                                                            )}
                                                        </TableCell>
                                                        <TableCell
                                                            align="right"
                                                            className={
                                                                classes.tableCell
                                                            }
                                                        >
                                                            {kit.StatusUpdateDate && (
                                                                <>
                                                                    {`${new Date(kit.StatusUpdateDate).toLocaleDateString()} ${new Date(kit.StatusUpdateDate).toLocaleTimeString()}`}
                                                                </>
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CustomTabPanel>
                        <CustomTabPanel value={tabIndex} index={1}>
                            <div className={classes.filesTab}>
                                Test Reports
                                <div className={classes.filesContainer}>
                                    {kitFiles.map((fileList) =>
                                        fileList.Reports.map((report) => (
                                            <FileCard
                                                variant="report"
                                                url={report.FilePath}
                                                title={getKitDisplayName(
                                                    getKitById(
                                                        kits,
                                                        fileList.KitId
                                                    ),
                                                    kitTypes
                                                )}
                                                date={report.ReleaseDate}
                                                key={report.FilePath}
                                                isAdmin={isAdmin}
                                                fileName={report.FileName}
                                            />
                                        ))
                                    )}
                                </div>
                            </div>
                        </CustomTabPanel>
                        <CustomTabPanel value={tabIndex} index={2}>
                            <div className={classes.filesTab}>
                                Test Raw Data
                                <div className={classes.filesContainer}>
                                    {kitFiles.map((fileList) =>
                                        fileList.RawDataFiles.map((raw) => (
                                            <FileCard
                                                variant="raw"
                                                url={raw.FilePath}
                                                title={getKitDisplayName(
                                                    getKitById(
                                                        kits,
                                                        fileList.KitId
                                                    ),
                                                    kitTypes
                                                )}
                                                date={raw.ReleaseDate}
                                                key={raw.FilePath}
                                                isAdmin={isAdmin}
                                                fileName={raw.FileName}
                                            />
                                        ))
                                    )}
                                </div>
                            </div>
                        </CustomTabPanel>
                        <CustomTabPanel value={tabIndex} index={3}>
                            <div className={classes.filesTab}>
                                Medical Records/Reports
                            </div>
                        </CustomTabPanel>
                    </Box>
                </div>

                {kit && (
                    <>
                        <RegisterKitForm
                            open={registerKitDialogOpen}
                            onClose={handleRegisterKitDialogClose}
                            kit={kit}
                            onKitRegistered={handleKitRegistered}
                            isAdmin={isAdmin}
                        />
                        <UpdateLabProcessStatusForm
                            open={updateLabStatusDialogOpen}
                            onClose={handleUpdateLabStatusDialogClose}
                            kit={kit}
                            onLabProcessStatusUpdated={handleKitRegistered}
                        />
                        <UploadTestFilesDialog
                            kitId={kit.KitId}
                            open={uploadTestFilesDialogOpen}
                            onClose={handleUploadTestFilesDialogClose}
                            onSubmit={handleTestFilesUploaded}
                        />
                    </>
                )}
                <NewKitForm
                    open={newTestDialogOpen}
                    onClose={handleNewTestDialogClose}
                    memberId={member.MemberId}
                    onTestAdded={handleTestAdded}
                    isAdmin={isAdmin}
                />
                <KitProgress
                    open={kitProgressOpen}
                    onClose={() => {
                        setKitProgressOpen(false);
                        setKit(undefined);
                    }}
                    kit={kit}
                    isAdmin={isAdmin}
                />
                {kit && (
                    <ViewTRFDialog
                        kit={kit}
                        open={viewTRFDialogOpen}
                        onClose={() => setViewTRFDialogOpen(false)}
                        onSubmit={() => {
                            setKitRegistered(true);
                            setKit(undefined);
                            setViewTRFDialogOpen(false);
                        }}
                    />
                )}
                {kit && (
                    <UpdateTRFDialog
                        kitId={kit.KitId}
                        open={isUpdateTRFDialogOpen}
                        onClose={() => setIsUpdateTRFDialogOpen(false)}
                        onSubmit={() => {
                            setKitRegistered(true);
                            setKit(undefined);
                            setIsUpdateTRFDialogOpen(false);
                        }}
                    />
                )}
            </>
        )
    );
};

const useStyles = makeStyles((theme) => ({
    root: {
        padding: '20px',
        marginRight: '40px',
    },
    top: {
        display: 'flex',
        padding: '34px 0px 0 20px',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '28px',
        flex: '1 0 0',
        alignSelf: 'stretch',
        backgroundColor: theme.palette.primary.lighter,
    },
    memberNav: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
    },
    breadcrumbs: {
        display: 'flex',
        paddingLeft: '12px',
        alignItems: 'center',
        gap: '12px',
    },
    memberProfile: {
        display: 'flex',
        padding: '32px 48px',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
        alignSelf: 'stretch',
        borderRadius: '20px',
        backgroundColor: '#FFF',
    },
    memberProfileContainer: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '62px',
    },
    profilePicture: (props: { url: string }) => ({
        display: 'flex',
        height: '246px',
        paddingRight: '6px',
        alignItems: 'center',
        borderRadius: '200px',
        background: '#F7FAFF',
    }),
    face: {
        width: '223px',
        height: '223px',
        aspectRatio: '1/1',
    },
    memberDetails: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '94px',
        color: theme.palette.primary.main,
    },
    header: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '10px',
        alignSelf: 'stretch',
        color: theme.palette.primary.main,
    },
    name: {
        color: theme.palette.primary.main,
        fontFamily: 'Poppins',
        fontSize: '32px',
        fontStyle: 'normal',
        fontWeight: 500,
        lineHeight: '130%',
        letterSpacing: '-0.64px',
    },
    label: {
        color: theme.palette.primary.main,
        fontFamily: 'Noto Sans',
        fontSize: '16px',
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: '150%',
    },
    data: {
        color: theme.palette.primary.main,
        fontFamily: 'Noto Sans',
        fontSize: '16px',
        fontStyle: 'normal',
        fontWeight: 600,
        lineHeight: '150%',
        marginRight: '20px',
    },
    tests: {
        color: theme.palette.primary.main,
        fontFamily: 'Noto Sans',
        fontSize: '18px',
        fontStyle: 'normal',
        fontWeight: 700,
        lineHeight: '150%',
        letterSpacing: '-0.324px',
    },
    testsStats: {
        color: theme.palette.primary.main,
        fontFamily: 'Noto Sans',
        fontSize: '18px',
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: '150%',
        letterSpacing: '-0.324px',
    },
    patientInfo: {
        display: 'flex',
        gap: '8px',
        alignSelf: 'stretch',
        color: theme.palette.primary.main,
        flexDirection: 'column',
        alignItems: 'flex-start',
    },
    frame218: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'flex-end',
        gap: '151px',
    },
    lastUpdate: {
        alignSelf: 'stretch',
        color: '#9295A9',
        fontFamily: 'Noto Sans',
        fontSize: '16px',
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: '150%',
        letterSpacing: '-0.288px',
    },
    button: {
        display: 'flex',
        height: '44px',
        padding: '7px 24px',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '24px',
        alignSelf: 'stretch',
        borderRadius: '8px',
        backgroundColor: theme.palette.primary.main,
    },
    frame140: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
    },
    tabContent: {
        padding: '28px',
        borderRadius: '0px 10px 10px 10px',
        backgroundColor: '#FFF',
    },
    register: {
        display: 'flex',
        padding: '4px 16px',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '10px',
        borderRadius: '6px',
        background: '#D2EBD8',
        color: '#000',
        fontFamily: 'Open Sans',
        fontSize: '12px',
        fontStyle: 'normal',
        fontWeight: 600,
        lineHeight: '150%',
        cursor: 'pointer',
    },
    tableContainer: {
        width: '100%',
        overflowX: 'auto',
        borderRadius: 12,
        backgroundColor: '#FFF',
    },
    table: {
        minWidth: 800,
        width: '100%',
    },
    tableHeadRow: {
        backgroundColor: theme.palette.primary.background,
        height: 49,
    },
    tableCellHead: {
        fontWeight: 500,
        fontSize: 14,
        lineHeight: '150%',
        fontFamily: 'Noto Sans',
        color: theme.palette.primary.main,
        borderBottom: 'none',
        fontStyle: 'normal',
    },
    tab: {
        width: 191,
        height: 48,
        borderRadius: '16px 16px 0 0',
        backgroundColor: theme.palette.primary.light,
        color: theme.palette.primary.main,
        fontFamily: 'Poppins',
        fontSize: 16,
        fontStyle: 'normal',
        fontWeight: 500,
        lineHeight: '150%',
        textTransform: 'none',
        opacity: 1,
    },
    activeTab: {
        backgroundColor: '#FFF',
    },
    tableCell: {
        borderBottom: 'none',
        color: theme.palette.gray.dark,
        fontFamily: 'Noto Sans',
        fontSize: '14px',
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: '120%',
    },
    patientInfoRow: {
        display: 'flex',
        alignItems: 'left',
        gap: '20px',
    },
    backButton: {
        display: 'flex',
        height: '36px',
        padding: '4px 24px',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '10px',
        borderRadius: '30px',
        backgroundColor: '#FFF',
        color: theme.palette.primary.main,
        fontFamily: 'Noto Sans',
        fontSize: '16px',
        fontStyle: 'normal',
        fontWeight: 600,
        lineHeight: '150%',
        cursor: 'pointer',
    },
    filesTab: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '20px',
        alignSelf: 'stretch',
        color: theme.palette.primary.main,
        fontFamily: 'Noto Sans',
        fontSize: '16px',
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: '150%',
    },
    filesContainer: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '20px',
        flexWrap: 'wrap',
    },
    newIcon: {
        color: theme.palette.warning.light,
        marginLeft: '5px',
    },
    validIcon: {
        color: theme.palette.success.light,
        marginLeft: '5px',
    },
    invalidIcon: {
        color: theme.palette.error.light,
        marginLeft: '5px',
    },
}));

export default MemberPage;
